import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useRedirectResult } from './hooks/useRedirectResult';
import { useAppStore } from './stores';
import { LoginPage } from './components/LoginPage';
import { DashboardPage } from './components/DashboardPage';
import { HouseholdsPage } from './components/HouseholdsPage';
import { ImportPage } from './components/ImportPage';
import { AboutPage } from './components/AboutPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MainLayout } from './components/MainLayout';
/**
 * Main App Component
 * Handles authentication state initialization and routing
 */
function App() {
  const { loading, isAuthenticated } = useAuth();
  const { initApp } = useAppStore();

  // Catch mobile / installed-PWA users returning from a signInWithRedirect.
  useRedirectResult();

  // Initialize auth store on app mount
  useEffect(() => {
    const unsubscribe = initApp();
    return () => {
      unsubscribe?.();
    };
  }, [initApp]);

  // Show loading screen while checking authentication state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950" role="status" aria-label="Loading Chores Champ">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-gray-200 border-t-indigo-600 dark:border-gray-800 dark:border-t-indigo-400 motion-reduce:animate-none" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Signing in creates the account, with a code (LoginPage); there's no separate sign-up. */}
        <Route path="/signup" element={<Navigate to="/login" replace />} />
{/* 2. Guarded Layout Group */}
        {/* We wrap the entire layout in the ProtectedRoute */}
        <Route
          path="/"
          element={
            <ProtectedRoute fallback={<Navigate to="/login" replace />}>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* 3. Nested Page Routes */}
          {/* These will render inside the MainLayout's <Outlet /> */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="households" element={<HouseholdsPage />} />
          <Route path="import" element={<ImportPage />} />
          <Route path="about" element={<AboutPage />} />
          
          {/* Example of a Role-Specific Route inside the layout 
          <Route 
            path="admin" 
            element={
              <ProtectedRoute requiredRole={UserRole.ADMIN}>
                <AdminPage />
              </ProtectedRoute>
            } 
          />*/}
        </Route>
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
